import React, { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { is8DataApp, logger, pathname, urlValidation } from '../../../utils';
import { updateCompany } from '../../API/company';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { useOnboardingContext } from '../../../contexts/Onboarding';
import { useCompanyContext } from '../../../contexts/Company';
import { useEventTrackerContext } from '../../../contexts/EventTracker';
import { Field } from 'formik';
import { TextField } from 'formik-material-ui';
import * as yup from 'yup';
import messages from '../../../messages';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Step from '../BuildingBlocks/Stepper/Step';
import { getShopifyAccounts, getShopifyAuthUrl } from '../../API/shopify';
import { useMobile } from 'frontend/src/hooks/useMobile';

const useStyles = makeStyles((theme: any) => ({
  fieldContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  field: { maxWidth: 400 },
  message: {
    marginTop: theme.spacing(5),
  },
  errorText: {
    color: theme.palette.brand.red,
  },
}));

const nextStep = pathname.onboarding.extras.companyName;
const nextStep8Data = pathname.onboarding.main.product;

export default function StoreUrl() {
  const classes = useStyles();
  const snackbar = useSnackbarContext();
  const history = useHistory();
  const mobile = useMobile();
  const { trackEvent } = useEventTrackerContext();
  const { setLoading, setActiveStep } = useOnboardingContext();
  const { business, setBusiness } = useCompanyContext();

  const updateData = useCallback(
    async (url: string, companyName: string = null) => {
      if (business.storeUrl !== url) {
        setLoading(true);
        let data = { store_url: url };
        if (companyName) {
          data['name'] = companyName;
        }
        await updateCompany(data);
        setBusiness({ ...business, ...data });
      }
      trackEvent({ event: 'entered_store_url', store_url: url });
    },
    [business, setBusiness, setLoading, trackEvent],
  );

  const getShopifyAccountsCallback = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getShopifyAccounts();
      if (result.accounts.length > 0) {
        const shop = result.accounts[0].shop;
        const companyName = shop?.split('.')[0] || '';
        await updateData(`https://${shop}`, companyName);
        setActiveStep(nextStep8Data.stepInStepper);
        history.push(nextStep8Data.url);
      }
    } catch (error) {
      logger.error({ message: 'failed to get shopify account', error });
    }
    setLoading(false);
  }, [history, setActiveStep, setLoading, updateData]);

  useEffect(() => {
    if (is8DataApp()) {
      getShopifyAccountsCallback();
    }
  }, [getShopifyAccountsCallback]);

  async function handleSubmit(values: { url: string }) {
    try {
      if (is8DataApp()) {
        await onSubmit8data(values.url);
      } else {
        await updateData(values.url);
        setLoading(false);
        setActiveStep(nextStep.stepInStepper);
        history.push(nextStep.url);
      }
    } catch (error) {
      setLoading(false);
      snackbar.show(messages.general.saveError, 'error');
    }
  }

  async function onSubmit8data(url: string) {
    const redirectUrl = await getShopifyAuthUrl(url);
    handleConnect(redirectUrl.auth_url);
  }

  function checkWindowRefBlocked(windowRef): boolean {
    if (windowRef === null) {
      snackbar.show(
        'Please enable pop-ups for this site. Reload and try again.',
        'error',
      );
      setLoading(false);
      return true;
    } else {
      return false;
    }
  }

  async function handleConnect(url: string) {
    trackEvent({
      event: 'click_connect_shopify_account',
    });

    if (mobile) {
      window.location.href = url;
    } else {
      const newWindowRef = window.open(url);
      if (checkWindowRefBlocked(newWindowRef)) {
        return;
      }
    }
  }

  return (
    <Step
      initialValues={{ url: business.storeUrl || '' }}
      validationSchema={yup.object({
        url: urlValidation,
      })}
      onSubmit={handleSubmit}
      component={() => {
        return (
          <div className={classes.fieldContainer}>
            <Field
              name="url"
              label="Store URL"
              component={TextField}
              variant="outlined"
              fullWidth
              className={classes.field}
            />
          </div>
        );
      }}
    />
  );
}
